import {ChevronLeft, ChevronRight, FirstPage, LastPage} from '@mui/icons-material';
import {Box, Button, ButtonGroup, useMediaQuery, useTheme} from '@mui/material';
import {useCallback, useMemo} from 'react';
import {StudyContent} from '@cp/model';
import {useEventListener} from '../../../bonds/bondImageUpload/useEventListener';
import {env} from '../../../config/env';
import {buildArrayNumber} from '../../../utils/other/buildArrayNumber';

export interface AdminStudyEditorNavigationProps {
  sections: StudyContent[];
  currentSectionIndex: number;
  onPrevious: () => void;
  onNext: () => void;
  onJumpToSection: (index: number) => void;
  isFirstPage: boolean;
  isLastPage: boolean;
}

export default function AdminStudyEditorNavigation({
  sections,
  currentSectionIndex,
  onPrevious,
  onNext,
  onJumpToSection,
}: AdminStudyEditorNavigationProps) {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  const visibleIndices = useMemo(() => {
    return buildArrayNumber(
      Math.max(
        1,
        currentSectionIndex - (isDesktop ? env.study.screenNavigation.lg : env.study.screenNavigation.md) + 1,
      ),
      Math.min(
        sections.length,
        currentSectionIndex + (isDesktop ? env.study.screenNavigation.lg : env.study.screenNavigation.md) + 1,
      ),
    );
  }, [sections, currentSectionIndex, isDesktop]);

  // Keyboard navigation
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      // Don't navigate if user is typing in an input field
      const target = event.target as HTMLElement;
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable ||
        target.closest('input, textarea, [contenteditable="true"]')
      ) {
        return;
      }

      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        if (currentSectionIndex > 0) {
          onPrevious();
        }
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        if (currentSectionIndex < sections.length - 1) {
          onNext();
        }
      }
    },
    [currentSectionIndex, sections.length, onPrevious, onNext],
  );

  useEventListener(window, 'keydown', handleKeyDown);

  if (sections.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        p: 2,
        bgcolor: 'grey.50',
        borderRadius: 1,
      }}
    >
      <ButtonGroup variant="outlined" size="small">
        <Button onClick={() => onJumpToSection(1)} disabled={currentSectionIndex === 0} sx={{minWidth: 'auto', px: 1}}>
          <FirstPage />
        </Button>
        <Button onClick={onPrevious} disabled={currentSectionIndex === 0} sx={{minWidth: 'auto', px: 1}}>
          <ChevronLeft />
        </Button>
        {sections.map((section, index) => {
          if (visibleIndices.length > 0 && !visibleIndices.includes(index + 1)) {
            return null;
          }
          return (
            <Button
              key={section.title}
              onClick={() => onJumpToSection(index + 1)}
              variant={index === currentSectionIndex ? 'contained' : 'outlined'}
              sx={{
                minWidth: 'auto',
                px: 1.5,
                fontSize: '0.75rem',
              }}
            >
              {index + 1}
            </Button>
          );
        })}
        <Button onClick={onNext} disabled={currentSectionIndex === sections.length - 1} sx={{minWidth: 'auto', px: 1}}>
          <ChevronRight />
        </Button>
        <Button
          onClick={() => onJumpToSection(sections.length)}
          disabled={currentSectionIndex === sections.length - 1}
          sx={{minWidth: 'auto', px: 1}}
        >
          <LastPage />
        </Button>
      </ButtonGroup>
    </Box>
  );
}
